import * as BABYLON from 'babylonjs';
import { WorldModel, ToolType } from '../models/WorldModel';
import { WorldController } from '../controllers/WorldController';
import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';
import { BRUSH_RADIUS } from '../models/constants';

/**
 * Owns all raw input handling: pointer raycasting for terrain interaction
 * and Alt-drag rotation for the camera. Delegates game actions to
 * WorldController rather than applying them directly.
 */
export class InputService {
    private scene: BABYLON.Scene;
    private canvas: HTMLCanvasElement;
    private camera: BABYLON.ArcRotateCamera;
    private worldModel: WorldModel;
    private pubsub: PubSub<GameEvents>;
    private groundMesh: BABYLON.Mesh;
    private worldController: WorldController;

    private isPointerDown = false;
    private lastHitPoint: BABYLON.Vector3 | null = null;
    private brushInterval: ReturnType<typeof setInterval> | null = null;
    private readonly BRUSH_CADENCE_MS = 200;

    constructor(
        scene: BABYLON.Scene,
        canvas: HTMLCanvasElement,
        camera: BABYLON.ArcRotateCamera,
        worldModel: WorldModel,
        pubsub: PubSub<GameEvents>,
        groundMesh: BABYLON.Mesh,
        worldController: WorldController
    ) {
        this.scene = scene;
        this.canvas = canvas;
        this.camera = camera;
        this.worldModel = worldModel;
        this.pubsub = pubsub;
        this.groundMesh = groundMesh;
        this.worldController = worldController;

        this.setupPointerInput();
        this.setupAltDragRotation();
    }

    /**
     * Applies the active terrain tool at the given world point by delegating
     * to the appropriate `WorldController` method.
     */
    private applyTool(point: BABYLON.Vector3): void {
        switch (this.worldModel.currentTool) {
            case ToolType.Lower:
                this.worldController.lowerLand(point);
                break;
            case ToolType.Flatten:
                this.worldController.flattenLand(point);
                break;
            case ToolType.SpawnBeast:
                this.worldController.spawnBeastAt(point);
                break;
            case ToolType.Raise:
            default:
                this.worldController.raiseLand(point);
                break;
        }
    }

    /**
     * Registers a Babylon pointer observable that:
     * - Publishes `world:brush-update` / `world:brush-hide` on pointer move,
     *   and tracks the current hit point for use by the held-brush interval.
     * - On POINTERDOWN (non-Alt), immediately fires the active tool and starts
     *   a ~50ms interval that reapplies it while the pointer remains held.
     * - On POINTERUP, clears the interval.
     */
    private setupPointerInput(): void {
        this.scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERMOVE) {
                const pickResult = this.scene.pick(this.scene.pointerX, this.scene.pointerY);
                if (pickResult?.hit && pickResult.pickedMesh === this.groundMesh) {
                    this.pubsub.publish('world:brush-update', { center: pickResult.pickedPoint!, radius: BRUSH_RADIUS });
                    if (this.isPointerDown) {
                        this.lastHitPoint = pickResult.pickedPoint!;
                    }
                } else {
                    this.pubsub.publish('world:brush-hide');
                    if (this.isPointerDown) {
                        this.lastHitPoint = null;
                    }
                }
            }

            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
                const downEvt = pointerInfo.event as PointerEvent;
                if (downEvt.altKey || downEvt.button !== 0) return;

                this.isPointerDown = true;

                if (pointerInfo.pickInfo?.hit && pointerInfo.pickInfo.pickedMesh === this.groundMesh) {
                    this.lastHitPoint = pointerInfo.pickInfo.pickedPoint!;
                    this.applyTool(this.lastHitPoint);
                }

                this.brushInterval = setInterval(() => {
                    if (this.lastHitPoint) {
                        this.applyTool(this.lastHitPoint);
                    }
                }, this.BRUSH_CADENCE_MS);
            }

            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERUP) {
                if ((pointerInfo.event as PointerEvent).button !== 0) return;
                this.isPointerDown = false;
                this.lastHitPoint = null;
                if (this.brushInterval !== null) {
                    clearInterval(this.brushInterval);
                    this.brushInterval = null;
                }
            }
        });
    }

    /**
     * Rotates the camera by holding Alt and dragging. The rotation angle is
     * derived from the change in pointer angle relative to the screen centre,
     * so dragging feels like physically spinning the terrain underhand.
     */
    private setupAltDragRotation(): void {
        let altDragging = false;
        let startAngle = 0;
        let startCameraAlpha = 0;

        const screenAngle = (clientX: number, clientY: number) => {
            const cx = window.innerWidth / 2;
            const cy = window.innerHeight / 2;
            return Math.atan2(clientY - cy, clientX - cx);
        };

        this.canvas.addEventListener('pointerdown', (evt) => {
            if (evt.altKey) {
                altDragging = true;
                startAngle = screenAngle(evt.clientX, evt.clientY);
                startCameraAlpha = this.camera.alpha;
                this.camera.detachControl();
                evt.preventDefault();
            }
        });

        this.canvas.addEventListener('pointermove', (evt) => {
            if (altDragging) {
                const currentAngle = screenAngle(evt.clientX, evt.clientY);
                const deltaAngle = currentAngle - startAngle;
                this.camera.alpha = startCameraAlpha + deltaAngle;
                evt.preventDefault();
            }
        });

        const stopAltDrag = () => {
            if (altDragging) {
                altDragging = false;
                this.camera.attachControl(this.canvas, true);
            }
        };
        this.canvas.addEventListener('pointerup', stopAltDrag);
        this.canvas.addEventListener('pointerleave', stopAltDrag);
    }
}
